import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Send, Paperclip, X, FileText, File as FileIcon, Download } from "lucide-react";
import { io } from "socket.io-client";
import "../assets/css/SupportTicketChat.css";
import { formatTicketPid } from "../utils/formatPid";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const STATUS_STYLE = {
  open:       { bg: "#eff6ff", color: "#2563eb" },
  processing: { bg: "#fff7ed", color: "#ea580c" },
  closed:     { bg: "#f1f5f9", color: "#64748b" },
};
const STATUS_LABEL = { open: "Open", processing: "Processing", closed: "Closed" };

const FILE_TYPE_META = {
  "application/pdf": { label: "PDF Document", icon: FileText, ext: "pdf" },
  "application/msword": { label: "Word Document", icon: FileText, ext: "doc" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": { label: "Word Document", icon: FileText, ext: "docx" },
  "application/vnd.ms-excel": { label: "Excel Spreadsheet", icon: FileText, ext: "xls" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": { label: "Excel Spreadsheet", icon: FileText, ext: "xlsx" },
};

const getFileMeta = (mimeType) => FILE_TYPE_META[mimeType] || { label: "Document", icon: FileIcon, ext: null };

const MediaImage = ({ mediaPid, mediaUrl, token, onOpen }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (mediaUrl) { setSrc(mediaUrl); return; }
    fetch(`${BASE_URL}/api/v1/uploads/media/${mediaPid}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.blob()).then(b => setSrc(URL.createObjectURL(b))).catch(() => {});
  }, [mediaPid, mediaUrl, token]);
  return src ? (
    <img
      src={src}
      alt="attachment"
      className="stc-upload-img"
      style={{ cursor: "zoom-in" }}
      onClick={() => onOpen?.(src)}
    />
  ) : <span className="text-muted" style={{ fontSize: 12 }}>Loading...</span>;
};

const MediaFile = ({ mediaPid, mimeType }) => {
  const token = localStorage.getItem("token");
  const [opening, setOpening] = useState(false);
  const { icon: Icon, ext } = getFileMeta(mimeType);
  const baseName = mediaPid ? mediaPid.toUpperCase() : "Document";
  const displayName = ext ? `${baseName}.${ext}` : baseName;

  const openPreview = async () => {
    setOpening(true);
    try {
      const res = await fetch(`${BASE_URL}/api/v1/uploads/media/${mediaPid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      // ignore
    } finally {
      setOpening(false);
    }
  };

  return (
    <button type="button" className="stc-file-card" onClick={openPreview} disabled={opening}>
      <span className="stc-file-card-icon"><Icon size={18} /></span>
      <span className="stc-file-card-info">
        <span className="stc-file-card-name" title={displayName}>
          {opening ? "Opening..." : displayName}
        </span>
        <span className="stc-file-card-hint"><Download size={11} className="me-1" />Tap to open &amp; download</span>
      </span>
    </button>
  );
};

const ImageLightbox = ({ src, onClose }) => {
  if (!src) return null;
  return (
    <div className="stc-lightbox-overlay" onClick={onClose}>
      <button className="stc-lightbox-close" onClick={onClose} aria-label="Close">
        <X size={22} />
      </button>
      <img src={src} alt="attachment full view" className="stc-lightbox-img" onClick={(e) => e.stopPropagation()} />
    </div>
  );
};

const SupportTicketChat = () => {
  const location = useLocation();
  const { ticketId } = useParams();
  const token = localStorage.getItem("token");

  const [myUserPid, setMyUserPid] = useState(null);
  const myUserPidRef = useRef(null);
  const [ticket, setTicket] = useState(location.state?.ticket || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const isSuperAdmin = localStorage.getItem("role") === "super_admin";

  const chatRef        = useRef(null);
  const fileRef        = useRef(null);
  const socketRef      = useRef(null);
  const topSentinelRef = useRef(null);
  const msgPageRef     = useRef(1);
  const sentMsgIds     = useRef(new Set());
  const hasMoreRef   = useRef(false);
  const loadingMoreRef = useRef(false);
  const ticketPid = ticket?.ticketPid || ticketId;

  // Fetch logged-in user's pid
  useEffect(() => {
    fetch(`${BASE_URL}/api/v1/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      if (data?.response?.userPid) {
        myUserPidRef.current = data.response.userPid;
        setMyUserPid(data.response.userPid);
      }
    }).catch(() => {});
  }, [token]);

  // Fetch ticket details
  useEffect(() => {
    if (!ticketPid) return;
    fetch(`${BASE_URL}/api/v1/support-tickets/${ticketPid}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(data => {
      if (data.status) {
        setTicket(data.response);
        setIsClosed(data.response?.status === "closed");
      }
    }).catch(() => {});
  }, [ticketPid, token]);

  // Fetch messages
  const fetchMessages = useCallback(async (pg = 1, prepend = false) => {
    if (!ticketPid) return;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/support-tickets/${ticketPid}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ page: pg, limit: 30 }),
      });
      const data = await res.json();
      if (data.status) {
        const items = [...(data.response?.items || [])].reverse();
        const hasNext = data.response?.pagination?.hasNextPage || false;
        setHasMore(hasNext);
        hasMoreRef.current = hasNext;
        if (prepend) {
          // preserve scroll position when prepending older messages
          const chat = chatRef.current;
          const prevHeight = chat ? chat.scrollHeight : 0;
          setMessages(prev => [...items, ...prev]);
          setTimeout(() => {
            if (chat) chat.scrollTop = chat.scrollHeight - prevHeight;
          }, 0);
        } else {
          setMessages(items);
          setTimeout(() => {
            if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
          }, 50);
        }
      }
    } catch {}
  }, [ticketPid, token]);

  useEffect(() => {
    msgPageRef.current = 1;
    fetchMessages(1, false);
  }, [fetchMessages]);

  // IntersectionObserver on top sentinel — load older messages when scrolled to top
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMoreRef.current && !loadingMoreRef.current) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
        const next = msgPageRef.current + 1;
        msgPageRef.current = next;
        fetchMessages(next, true).finally(() => {
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchMessages]);

  // Socket.io
  useEffect(() => {
    if (!ticketPid || !token) return;
    // Super admin session forces long-polling — websocket upgrade was unreliable behind their proxy setup.
    const socket = isSuperAdmin
      ? io(`${BASE_URL}/support`, { auth: { token }, transports: ["polling"] })
      : io(`${BASE_URL}/support`, { auth: { token } });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("support:join", { ticketPid });
    });

    socket.on("support:message", (msg) => {
      // Drop if already tracked in sentMsgIds ref (file upload response) or already in state
      if (msg.messageId && sentMsgIds.current.has(msg.messageId)) return;
      setMessages(prev => {
        if (msg.messageId && prev.some(m => m.messageId === msg.messageId)) return prev;
        return [...prev, { ...msg, _fromSocket: true }];
      });
      setTimeout(() => {
        if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
      }, 50);
    });

    socket.on("support:error", ({ message }) => {
      console.error("Support socket error:", message);
    });

    return () => {
      socket.emit("support:leave");
      socket.disconnect();
    };
  }, [ticketPid, token]);


  const sendTextMessage = async () => {
    const text = input.trim();
    if (!text || isClosed) return;
    setSending(true);
    // Use socket for text-only messages
    socketRef.current?.emit("support:message", { message: text });
    setInput("");
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendTextMessage(); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || isClosed) return;
    e.target.value = "";

    const isImage = file.type.startsWith("image/");
    const localUrl = URL.createObjectURL(file);
    const caption = input.trim();
    const tempMsg = {
      _tempId: Date.now(),
      message: caption || null,
      mediaPid: null,
      _localFile: { url: localUrl, name: file.name, isImage },
      mimeType: file.type,
      sender: { userPid: myUserPidRef.current || myUserPid },
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempMsg]);
    if (caption) setInput("");
    setTimeout(() => {
      if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }, 50);

    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${BASE_URL}/api/v1/support-tickets/${ticketPid}/send-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          file: base64,
          ...(caption ? { message: caption } : {}),
        }),
      });
      const data = await res.json();
      if (data.status && data.response) {
        if (data.response.messageId) sentMsgIds.current.add(data.response.messageId);
        setMessages(prev => {
          const deduped = prev.filter(m => !(m._fromSocket && m.messageId === data.response.messageId));
          return deduped.map(m => m._tempId === tempMsg._tempId ? { ...data.response, _fromSocket: false } : m);
        });
      } else if (data.status) {
        setMessages(prev => prev.filter(m => m._tempId !== tempMsg._tempId));
      }
    } catch {
      // keep temp message visible on error
    }
  };

  const closeTicket = async () => {
    if (!ticketPid) return;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/support-tickets/${ticketPid}/close`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.status) {
        setTicket(prev => ({ ...prev, status: "closed" }));
        setIsClosed(true);
      }
    } catch {}
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const isMyMessage = (msg) => {
    const uid = myUserPidRef.current || myUserPid;
    if (!uid || !msg.sender?.userPid) return false;
    return msg.sender.userPid === uid;
  };

  return (
    <main className="app-wrapper stc-page">
      <div className="stc-wrapper">

        {/* Ticket info bar */}
        {ticket && (
          <div className="stc-info-bar">
            <div className="stc-info-left">
              <span className="stc-ticket-id">{formatTicketPid(ticket.ticketPid || ticket.id)}</span>
              <span className="stc-ticket-subject">{ticket.subject}</span>
            </div>
            <div className="stc-info-right">
              <span
                className="stc-badge"
                style={{
                  background: STATUS_STYLE[ticket.status]?.bg || "#f1f5f9",
                  color: STATUS_STYLE[ticket.status]?.color || "#64748b",
                }}
              >
                {STATUS_LABEL[ticket.status] || ticket.status}
              </span>
              <span className="stc-date">{formatDate(ticket.createdAt || ticket.date)}</span>
              {isSuperAdmin && !isClosed && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  style={{ fontSize: 12, padding: "3px 12px", borderRadius: 8 }}
                  onClick={closeTicket}
                >
                  Close Ticket
                </button>
              )}
            </div>
          </div>
        )}

        {/* Top sentinel — triggers loading older messages on scroll to top */}
        <div ref={topSentinelRef} style={{ height: 1 }} />
        {loadingMore && (
          <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
            <div className="sess-spinner" />
          </div>
        )}

        {/* Chat area */}
        <div className="stc-chat" ref={chatRef}>
          {messages.map((msg, idx) => {
            const mine = isMyMessage(msg);
            const senderName = msg.sender ? `${msg.sender.firstName || ""} ${msg.sender.lastName || ""}`.trim() : "";
            const avatarLetter = senderName ? senderName[0].toUpperCase() : (mine ? "U" : "S");
            return (
              <div key={msg.messageId || msg._tempId || `msg-${idx}`} className={`stc-msg-row ${mine ? "stc-msg-row--user" : "stc-msg-row--support"}`}>
                <div className={`stc-avatar ${mine ? "stc-avatar--user" : "stc-avatar--support"}`}>
                  {avatarLetter}
                </div>
                <div className="stc-bubble-wrap">
                  {senderName && (
                    <span className={`stc-sender-name ${mine ? "stc-sender-name--user" : ""}`}>
                      {senderName}
                    </span>
                  )}
                  <div className={`stc-bubble ${mine ? "stc-bubble--user" : "stc-bubble--support"}`}>
                    {msg._localFile ? (
                      msg._localFile.isImage ? (
                        <img
                          src={msg._localFile.url}
                          alt={msg._localFile.name}
                          className="stc-upload-img"
                          style={{ cursor: "zoom-in" }}
                          onClick={() => setLightboxSrc(msg._localFile.url)}
                        />
                      ) : (
                        <span className="stc-file-link"><Paperclip size={13} className="me-1" />{msg._localFile.name}</span>
                      )
                    ) : (msg.mediaPid || msg.mediaUrl) ? (
                      msg.mimeType?.startsWith("image/") ? (
                        <MediaImage mediaPid={msg.mediaPid} mediaUrl={msg.mediaUrl} token={token} onOpen={setLightboxSrc} />
                      ) : (
                        <MediaFile mediaPid={msg.mediaPid} mimeType={msg.mimeType} />
                      )
                    ) : (
                      <p className="stc-bubble-text">{msg.message || msg.text}</p>
                    )}
                    {msg.message && msg._localFile && (
                      <p className="stc-bubble-text mt-1">{msg.message}</p>
                    )}
                    <span className="stc-time">{formatTime(msg.createdAt) || msg.time}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Closed banner */}
        {isClosed && (
          <div className="stc-closed-banner">
            This ticket is closed. No further messages can be sent.
          </div>
        )}

        {/* Input */}
        {!isClosed && (
          <div className="stc-input-bar">
            <input
              type="file"
              ref={fileRef}
              className="d-none"
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleFileUpload}
            />
            <button className="stc-attach-btn" title="Upload file" onClick={() => fileRef.current.click()}>
              <Paperclip size={16} />
            </button>
            <input
              type="text"
              className="stc-input"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <button className="stc-send-btn" onClick={sendTextMessage} disabled={!input.trim() || sending}>
              <Send size={16} />
            </button>
          </div>
        )}

      </div>

      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </main>
  );
};

export default SupportTicketChat;
